import { devfolioFetch } from "./devfolioApi";

const normalizeUserStatus = (user) => {
  if (typeof user?.isActive === "boolean") return user.isActive;
  if (typeof user?.active === "boolean") return user.active;
  return true;
};

const normalizeEmail = (value) => {
  if (!value || typeof value !== "string") return "";
  return value.toLowerCase().trim();
};

const normalizeWorkspacePath = (workspaceKey = "") => {
  const key = String(workspaceKey || "").trim();
  if (!key) return "";
  return key.replace(/\/(users|profiles)\s*$/i, "");
};

/**
 * Fetches project users with access control
 * @param {string} workspaceKey - The workspace path (e.g., "workspaces/blindCv" or "IVR_Workspaces/main") or project name
 * @param {object} currentUser - The currently logged-in user (optional)
 * @param {object} projectFirebaseConfig - Optional firebase config for external project db
 * @returns {object} - { ok, users, hasAccess, reason }
 */
export async function getProjectUsers(workspaceKey, currentUser = null, projectFirebaseConfig = null) {
  try {
    const firebaseProjectId = String(projectFirebaseConfig?.firebaseProjectId || "").trim();
    const safeWorkspaceKey = normalizeWorkspacePath(workspaceKey);
    const apiRes = await devfolioFetch("/project-users", {
      method: "POST",
      body: JSON.stringify({
        workspaceKey: safeWorkspaceKey,
        firebaseProjectId,
      }),
    });

    const allUsers = Array.isArray(apiRes?.allUsers) ? apiRes.allUsers : [];
    const activeUsers = allUsers.filter((u) => normalizeUserStatus(u));
    const inactiveUsers = allUsers.filter((u) => !normalizeUserStatus(u));

    // Check if current user has access to this project
    let hasAccess = true;
    let accessReason = "";

    if (currentUser) {
      // Admins have access to all projects
      if (currentUser.role === "admin") {
        hasAccess = true;
      } else {
        // Check if user is in the project (using all users, not just active)
        const userInProject = allUsers.find(
          (u) => u.id === currentUser.uid || u.email === currentUser.email
        );

        if (!userInProject) {
          hasAccess = false;
          accessReason = "not_assigned";
        } else if (!normalizeUserStatus(userInProject)) {
          hasAccess = false;
          accessReason = "inactive";
        } else {
          hasAccess = true;
        }
      }
    }

    return {
      ok: true,
      users: activeUsers, // Return only active users
      allUsers: allUsers, // Return all users for admin purposes
      activeUsers,
      inactiveUsers,
      hasAccess,
      accessReason
    };
  } catch (err) {
    console.error("Error fetching project users:", err);
    return {
      ok: false,
      users: [],
      allUsers: [],
      activeUsers: [],
      inactiveUsers: [],
      hasAccess: false,
      accessReason: "error"
    };
  }
}

/**
 * Fetches only active users for a workspace (strict filter)
 * @param {string} workspaceKey - The workspace identifier
 * @param {object} projectFirebaseConfig - Optional firebase config for external project db
 * @returns {object} - { ok, users }
 */
export async function getActiveProjectUsers(workspaceKey, projectFirebaseConfig = null) {
  try {
    const result = await getProjectUsers(workspaceKey, null, projectFirebaseConfig);
    return { ok: result.ok, users: result.activeUsers || result.users || [] };
  } catch (err) {
    console.error("Error fetching active project users:", err);
    return { ok: false, users: [] };
  }
}


